/**
 * API Keys settings tab - manage API keys for external bot access
 */
import { useState } from 'react'
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks'
import { Button, Input, Modal, LoadingCard } from '../common'
import type { ApiKeyCreate, ApiKeyCreated } from '../../types'

export function ApiKeysTab() {
  const { data: apiKeys, isLoading } = useApiKeys()
  const createApiKey = useCreateApiKey()
  const revokeApiKey = useRevokeApiKey()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null)
  const [formData, setFormData] = useState<ApiKeyCreate>({
    name: '',
    expires_in_days: null,
  })
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const openCreateModal = () => {
    setFormData({ name: '', expires_in_days: null })
    setError('')
    setCreatedKey(null)
    setIsCreateModalOpen(true)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      const result = await createApiKey.mutateAsync(formData)
      setCreatedKey(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key')
    }
  }

  const handleCopyKey = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey.key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleRevoke = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
      return
    }

    try {
      await revokeApiKey.mutateAsync(keyId)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke API key')
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-amber-800">API Key Security</h3>
            <p className="mt-1 text-sm text-amber-700">
              API keys provide full read access to your portfolio data. Store them securely and never share them publicly.
              The full key is only shown once when created.
            </p>
          </div>
        </div>
      </div>

      {/* API Keys Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900">API Keys</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage API keys for external services like Clawdbot/Moltbot
            </p>
          </div>
          <Button onClick={openCreateModal}>Create API Key</Button>
        </div>

        <div className="divide-y divide-gray-200">
          {apiKeys?.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create an API key to enable external bot access to your portfolio data.
              </p>
              <div className="mt-4">
                <Button onClick={openCreateModal}>Create API Key</Button>
              </div>
            </div>
          ) : (
            apiKeys?.map((key) => (
              <div key={key.id} className="px-6 py-4 flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900">{key.name}</h3>
                    {!key.enabled && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                    <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{key.key_prefix}</code>
                    <span>Created: {formatDate(key.created_at)}</span>
                    <span>Last used: {formatDateTime(key.last_used_at)}</span>
                    {key.expires_at && (
                      <span className={new Date(key.expires_at) < new Date() ? 'text-red-600' : ''}>
                        Expires: {formatDate(key.expires_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  {key.enabled && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRevoke(key.id, key.name)}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create API Key Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={createdKey ? 'API Key Created' : 'Create API Key'}
      >
        {createdKey ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-800">Save your API key</h3>
                  <p className="mt-1 text-sm text-green-700">
                    Copy this key now. You won't be able to see it again!
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">API Key</label>
              <div className="flex gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded-md text-sm font-mono break-all">
                  {createdKey.key}
                </code>
                <Button onClick={handleCopyKey} variant="secondary">
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>

            <div className="text-sm text-gray-500 space-y-1">
              <p><strong>Name:</strong> {createdKey.name}</p>
              <p><strong>Prefix:</strong> {createdKey.key_prefix}</p>
              <p><strong>Expires:</strong> {createdKey.expires_at ? formatDate(createdKey.expires_at) : 'Never'}</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-gray-700 mb-1">Usage:</p>
              <code className="text-xs text-gray-600">
                curl -H "X-API-Key: {createdKey.key}" https://api.../bot/summary
              </code>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setIsCreateModalOpen(false)}>Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Clawdbot, Daily Reports"
              helperText="A descriptive name to identify this key"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration (optional)
              </label>
              <select
                value={formData.expires_in_days || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  expires_in_days: e.target.value ? parseInt(e.target.value) : null
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Never expires</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">1 year</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                For security, consider setting an expiration date
              </p>
            </div>

            {error && (
              <div className="text-sm text-red-600">{error}</div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsCreateModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createApiKey.isPending}>
                {createApiKey.isPending ? 'Creating...' : 'Create Key'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
